/**
 * WorkerFaceTemplate — Issue 4 (Path C 2026-05-22) dedicated page
 * for biometric face-template enrollment + consent.
 *
 * Pre-fix, face-template management was buried inside Profile →
 * Privacy and limited to consent grant/revoke; the actual upload
 * required the admin to act. This page consolidates all worker-side
 * actions:
 *
 *   1. Read the GDPR Art. 9(2)(a) explanation
 *   2. Pick a photo (file picker, JPEG / PNG / HEIC ≤ 5 MB)
 *   3. Click Upload — backend auto-grants consent on the same call
 *   4. Optionally revoke (deletes template + nulls consent)
 *
 * Linked from:
 *   - Sidebar → Account → Face matching (with badge when not enrolled)
 *   - Notification created by admin "Invite to upload" (link goes here)
 *   - Profile → Privacy section (deep-link instead of inline buttons)
 */

import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlined,
  DeleteOutlineOutlined,
  Face as FaceIcon,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { ApiError, meApi } from '../../api';
import type { MeProfile } from '../../api/me';
import GetAppCard from '../../components/face/GetAppCard';
import { ConfirmDialog } from '../../components/primitives';
import { activeLocale } from '../../utils/datetime';
import { translateApiError } from '../../i18n/translateApiError';
// Lazy so the heavy Amplify liveness deps are code-split out of the main bundle
// (only loaded when a worker actually opens the face check).
const FaceLivenessDialog = lazy(() => import('../../components/face/FaceLivenessDialog'));

const WorkerFaceTemplate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | 'revoke'>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [livenessOpen, setLivenessOpen] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const p = await meApi.getProfile();
      setProfile(p);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Object-URL cleanup removed with the file-upload flow — worker-web now
  // enrolls via in-browser liveness (the live scan is the photo; ADR-030).

  const isEnrolled = useMemo(
    () => Boolean(profile?.face_template_url),
    [profile],
  );
  const isConsented = useMemo(
    () => Boolean(profile?.face_template_consented_at),
    [profile],
  );

  const handleEnrolled = (fresh: MeProfile) => {
    setProfile(fresh);
    setLivenessOpen(false);
    setToast(t('worker:faceTemplate.toastSubmitted'));
  };

  const handleRevoke = async () => {
    if (busy) return;
    setBusy('revoke');
    try {
      const fresh = await meApi.faceTemplateConsent.revoke();
      setProfile(fresh);
      setRevokeOpen(false);
      setToast(t('worker:faceTemplate.toastConsentRevoked'));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('worker:faceTemplate.revokeFailed'));
      setRevokeOpen(false);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
        maxWidth: 720,
      }}
    >
      {/* §13 page chrome */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('worker:faceTemplate.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('worker:faceTemplate.subtitle')}
        </Typography>
      </Box>

      {/* Status banner — color-coded per current state */}
      <Card
        variant="outlined"
        sx={{
          width: '100%',
          mb: 2,
          p: 2,
          borderColor: isEnrolled ? 'success.light' : 'warning.light',
          bgcolor: isEnrolled ? 'success.lighter' : 'warning.lighter',
          // MUI v5 doesn't ship `lighter` by default — fall back to a
          // soft tint via rgba on the borderColor's hex. Inline style
          // for a tiny stable presentational difference.
        }}
        style={{
          backgroundColor: isEnrolled
            ? 'rgba(46,125,50,0.06)'
            : 'rgba(237,108,2,0.06)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {isEnrolled ? (
            <CheckCircleOutlined sx={{ color: 'success.main', mt: 0.25 }} />
          ) : (
            <WarningAmberOutlined sx={{ color: 'warning.main', mt: 0.25 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {isEnrolled
                ? t('worker:faceTemplate.active')
                : t('worker:faceTemplate.notSetUp')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isEnrolled
                ? t('worker:faceTemplate.statusBodyActive', {
                    date: profile?.face_template_consented_at
                      ? new Date(profile.face_template_consented_at).toLocaleDateString(activeLocale())
                      : t('worker:faceTemplate.recently'),
                  })
                : t('worker:faceTemplate.statusBodyNotSetUp')}
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* GDPR explanation card — always visible so the worker can re-read
          before deciding (or after, when considering revoke). */}
      <Card variant="outlined" sx={{ width: '100%', mb: 2, p: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <InfoOutlined sx={{ color: 'info.main', mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('worker:faceTemplate.aboutTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.55 }}
              dangerouslySetInnerHTML={{ __html: t('worker:faceTemplate.aboutBody') }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mt: 1, lineHeight: 1.55 }}
              dangerouslySetInnerHTML={{ __html: t('worker:faceTemplate.consentP2') }}
            />
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', mt: 1, lineHeight: 1.55, color: 'info.main', fontWeight: 600 }}>
              {t('worker:faceTemplate.mobileLivenessNote')}
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* Action card — upload OR replace + revoke depending on state */}
      <Card variant="outlined" sx={{ width: '100%', p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {isEnrolled ? t('worker:faceTemplate.manageTitle') : t('worker:faceTemplate.uploadTitle')}
        </Typography>

        {/* Existing template preview — shown when enrolled */}
        {isEnrolled && profile?.face_template_url && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar
              src={profile.face_template_url}
              sx={{ width: 80, height: 80, border: '2px solid', borderColor: 'success.light' }}
            >
              <FaceIcon />
            </Avatar>
            <Stack spacing={0.25}>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircleOutlined sx={{ fontSize: 14 }} />}
                label={t('worker:profile.currentlyEnrolled', { defaultValue: 'Currently enrolled' })}
                sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1, alignSelf: 'flex-start' }}
              />
              {profile.face_template_status === 'pending' && (
                <Chip size="small" color="warning" variant="outlined"
                  label={t('worker:faceTemplate.statusPending')}
                  sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1, alignSelf: 'flex-start' }} />
              )}
              {profile.face_template_status === 'approved' && (
                <Chip size="small" color="success"
                  label={t('worker:faceTemplate.statusApproved')}
                  sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1, alignSelf: 'flex-start' }} />
              )}
              {profile.face_template_status === 'rejected' && (
                <Chip size="small" color="error" variant="outlined"
                  label={t('worker:faceTemplate.statusRejected')}
                  sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1, alignSelf: 'flex-start' }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {t('worker:faceTemplate.replaceHint')}
              </Typography>
            </Stack>
          </Stack>
        )}

        {profile?.face_template_status === 'rejected' && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
            {profile.face_template_rejection_reason
              ? t('worker:faceTemplate.rejectedWithReason', { reason: profile.face_template_rejection_reason })
              : t('worker:faceTemplate.rejected')}
          </Alert>
        )}

        {/* In-browser liveness enrollment — the live camera scan IS the photo
            (no file pick), gated by the same liveness check as mobile so a
            saved photo can't be passed off as a live person (ADR-030). */}
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<FaceIcon fontSize="small" />}
              onClick={() => setLivenessOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 1 }}
            >
              {isEnrolled
                ? t('worker:faceTemplate.redoFaceCheck')
                : t('worker:faceTemplate.startFaceCheck')}
            </Button>
            {isEnrolled && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteOutlineOutlined fontSize="small" />}
                onClick={() => setRevokeOpen(true)}
                disabled={busy !== null}
                sx={{ textTransform: 'none', borderRadius: 1 }}
              >
                {t('worker:faceTemplate.revokeBtn')}
              </Button>
            )}
          </Stack>

          {!isEnrolled && (
            <>
              <Divider sx={{ mt: 1 }} />
              {isConsented ? (
                <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
                  {t('worker:faceTemplate.consentOnFileInfo')}
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary"
                  dangerouslySetInnerHTML={{ __html: t('worker:faceTemplate.uploadConsentHint') }}
                />
              )}
            </>
          )}
        </Stack>
      </Card>

      {/* Dedicated "get the mobile app" entry — license-aware QR + store
          links. Lets a desktop worker (no camera) finish on their phone. */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <GetAppCard brands={['qutime']} />
      </Box>

      {/* Back affordance */}
      <Button
        size="small"
        onClick={() => navigate('/me/profile')}
        sx={{ mt: 2, textTransform: 'none', fontSize: '0.8125rem' }}
      >
        {t('worker:faceTemplate.backToProfile')}
      </Button>

      <ConfirmDialog
        open={revokeOpen}
        tone="danger"
        title={t('worker:faceTemplate.revokeTitle')}
        description={t('worker:faceTemplate.revokeDesc')}
        details={t('worker:faceTemplate.revokeDetails')}
        confirmLabel={t('worker:faceTemplate.revokeConfirm')}
        confirmLoadingLabel={t('worker:faceTemplate.revokeLoading')}
        onConfirm={handleRevoke}
        onClose={() => setRevokeOpen(false)}
      />

      {livenessOpen && (
        <Suspense fallback={null}>
          <FaceLivenessDialog
            open={livenessOpen}
            onClose={() => setLivenessOpen(false)}
            onEnrolled={handleEnrolled}
          />
        </Suspense>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerFaceTemplate;
